// ============================================================================
// DoppelstundeInfoPanel — banners for consecutive lesson (Doppelstunde) info
// Shows: cancelled lesson banner, cancelled course banner, active course banner
// ============================================================================

interface NextLessonCourse {
  course_name: string;
  room_name?: string;
  teacher_names?: string;
  is_cancelled?: boolean;
  students: Array<{ id: string; name: string }>;
}

export interface DoppelstundeInfoPanelProps {
  nextLessonIsCancelled?: boolean;
  nextLessonSubject?: string | null;
  nextLessonCourses?: NextLessonCourse[];
}

export function DoppelstundeInfoPanel({
  nextLessonIsCancelled,
  nextLessonSubject,
  nextLessonCourses,
}: DoppelstundeInfoPanelProps) {
  const cancelledCourses = nextLessonCourses?.filter((c) => c.is_cancelled) ?? [];
  const activeCourses = nextLessonCourses?.filter((c) => !c.is_cancelled) ?? [];

  const hasAnything =
    nextLessonIsCancelled || cancelledCourses.length > 0 || activeCourses.length > 0;
  if (!hasAnything) return null;

  return (
    <>
      {/* Cancelled consecutive lesson (entire Doppelstunde) */}
      {nextLessonIsCancelled && (
        <div
          role="alert"
          className="rounded-lg border-2 border-red-500 bg-red-50 p-4 space-y-1"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">⚠️</span>
            <span className="font-semibold text-red-900 text-sm">
              Folgestunde abgesagt
            </span>
          </div>
          <p className="text-xs text-red-800">
            {nextLessonSubject
              ? `Die Folgestunde (${nextLessonSubject}) wurde abgesagt.`
              : "Die Folgestunde wurde abgesagt."}
            {" "}Alle Schüler bleiben in dieser Stunde.
          </p>
        </div>
      )}

      {/* Cancelled courses in the next period */}
      {cancelledCourses.length > 0 && (
        <div className="rounded-lg border-2 border-amber-500 bg-amber-50 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">⚠️</span>
            <span className="font-semibold text-amber-900 text-sm">
              Kurs in 2. Hälfte abgesagt
            </span>
          </div>
          <p className="text-xs text-amber-800">
            Der folgende Kurs in der nächsten Stunde wurde abgesagt. Die Schüler
            bleiben in dieser Stunde.
          </p>
          {cancelledCourses.map((course) => (
            <div
              key={course.course_name}
              className="rounded-md bg-amber-100 border border-amber-300 px-3 py-2"
            >
              <div className="font-medium text-amber-900 text-sm line-through">
                {course.course_name}
                {(course.room_name || course.teacher_names) && (
                  <span className="text-amber-600 ml-2 text-xs">
                    ({[course.room_name && `Raum ${course.room_name}`, course.teacher_names].filter(Boolean).join(" · ")})
                  </span>
                )}
                <span className="ml-2 text-xs font-normal no-underline text-red-600">
                  ABGESAGT
                </span>
              </div>
              <div className="text-xs text-amber-700 mt-1">
                {course.students
                  .map((s) => {
                    const p = s.name.split(" ");
                    return p.length > 1
                      ? `${p[0]} ${p[p.length - 1][0]}.`
                      : p[0];
                  })
                  .join(", ")}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Active courses in the next period */}
      {activeCourses.length > 0 && (
        <div className="rounded-lg border-2 border-blue-500 bg-blue-50 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">📚</span>
            <span className="font-semibold text-blue-900 text-sm">
              Kurs in 2. Hälfte dieses Unterrichts
            </span>
          </div>
          <p className="text-xs text-blue-800">
            Diese Schüler haben einen Kurs in der nächsten Stunde und werden
            nicht in die Folgestunde übernommen.
          </p>
          {activeCourses.map((course) => (
            <div
              key={course.course_name}
              className="rounded-md bg-blue-100 border border-blue-300 px-3 py-2"
            >
              <div className="font-medium text-blue-900 text-sm">
                {course.course_name}
                {(course.room_name || course.teacher_names) && (
                  <span className="text-blue-600 ml-2 text-xs">
                    ({[course.room_name && `Raum ${course.room_name}`, course.teacher_names].filter(Boolean).join(" · ")})
                  </span>
                )}
              </div>
              <div className="text-xs text-blue-700 mt-1">
                {course.students
                  .map((s) => {
                    const p = s.name.split(" ");
                    return p.length > 1
                      ? `${p[0]} ${p[p.length - 1][0]}.`
                      : p[0];
                  })
                  .join(", ")}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
